import { ReactNode } from "react";
import clsx from "clsx";

export default function KpiCard({
  label,
  value,
  icon,
  tone = "default",
  hint,
  onClick,
  active = false,
}: {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  tone?: "default" | "success" | "warn" | "danger" | "info";
  hint?: string;
  onClick?: () => void;
  active?: boolean;
}) {
  const toneRing = {
    default: "ring-slate-700/60",
    success: "ring-emerald-500/30",
    warn: "ring-amber-500/30",
    danger: "ring-red-500/30",
    info: "ring-sky-500/30",
  }[tone];
  const activeRing = {
    default: "ring-slate-300/70",
    success: "ring-emerald-300/80",
    warn: "ring-amber-300/80",
    danger: "ring-red-300/80",
    info: "ring-sky-300/80",
  }[tone];
  const activeBg = {
    default: "bg-slate-700/40",
    success: "bg-emerald-500/15",
    warn: "bg-amber-500/15",
    danger: "bg-red-500/15",
    info: "bg-sky-500/15",
  }[tone];
  const accent = {
    default: "text-slate-200",
    success: "text-emerald-300",
    warn: "text-amber-300",
    danger: "text-red-300",
    info: "text-sky-300",
  }[tone];
  const baseBg = "bg-[var(--color-surface)]/70";
  const ring = active ? activeRing + " ring-2" : toneRing;
  const interactive = !!onClick;

  const content = (
    <>
      <div className={clsx("text-xl shrink-0", accent)}>{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] sm:text-xs text-slate-400 uppercase tracking-wide truncate">
          {label}
        </div>
        <div
          className={clsx(
            "text-xl sm:text-2xl font-semibold leading-tight tabular-nums",
            accent,
          )}
        >
          {value}
        </div>
        {hint && (
          <div className="hidden sm:block text-xs text-slate-500 mt-0.5">{hint}</div>
        )}
      </div>
    </>
  );

  const className = clsx(
    "rounded-xl backdrop-blur ring-1 px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-2.5 sm:gap-3",
    active ? activeBg : baseBg,
    ring,
    interactive &&
      "cursor-pointer transition hover:bg-[var(--color-surface)]/90 active:scale-[0.99] focus:outline-none focus:ring-2 focus:ring-sky-400",
  );

  if (interactive) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={clsx(className, "text-left w-full")}
      >
        {content}
      </button>
    );
  }
  return <div className={className}>{content}</div>;
}
